import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { useQueryParams } from './use-query-params';

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export const reverseOrder: Record<SortOrder, SortOrder> = {
  [SortOrder.ASC]: SortOrder.DESC,
  [SortOrder.DESC]: SortOrder.ASC,
};

export type Sort<F extends string> = {
  field: F;
  order: SortOrder;
  update(field: F, sort: SortOrder): void;
};

const SortQueryParamsSchema = z.object({
  sort: z.string().optional(),
  order: z.nativeEnum(SortOrder).optional(),
});

export function useSort<F extends string, R extends {}, DF extends F>(
  results: R[],
  comparators: Record<F, (a: R, b: R) => number>,
  defaultField: DF,
  defaultOrder: SortOrder,
): [R[], Sort<F>] {
  const [params, queryParams] = useQueryParams(SortQueryParamsSchema);
  const field: F =
    params.sort && params.sort in comparators
      ? (params.sort as F)
      : defaultField;
  const order: SortOrder = params.order ?? defaultOrder;

  const sortedResults = useMemo(() => {
    const sorted = [...results];
    const comparator = comparators[field];
    sorted.sort((a, b) => {
      const comparison = comparator(a, b);
      return order === SortOrder.ASC ? comparison : -comparison;
    });
    return sorted;
  }, [results, field, order, comparators]);

  const update = useCallback(
    (field: F, order: SortOrder = SortOrder.ASC) => {
      queryParams.replace({ sort: field, order });
    },
    [queryParams],
  );

  return [sortedResults, { field, order, update }];
}
